import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import { PopupBackButton } from 'components/buttons/PopupBackButton';
import RedButton from 'components/buttons/RedButton';
import TextInputField from 'components/inputs/TextInputField';
import { H4 } from 'components/typography/Headers';
import { Text1 } from 'components/typography/Texts';
import { useLoginWithEmailPopup } from 'features/account/login-email/useLoginWithEmailPopup';

import { useForgotPasswordForm } from './useForgotPasswordForm';

const Container = styled.div`
  display: flex;
  flex-direction: column;
`;

const Header = styled(H4)`
  margin-bottom: 18px;
`;

const Description = styled(Text1)`
  margin-bottom: 24px;
`;

const EmailText = styled(Text1)`
  margin-top: -12px;
  margin-bottom: 24px;
  font-weight: 700;
  word-break: break-all;
`;

const EmailInput = styled(TextInputField)`
  margin-bottom: 12px;
`;

const SubmitButton = styled(RedButton)`
  margin-bottom: 12px;
`;

interface Props {
  onLogged?: () => void;
}

const ForgotPasswordPopup = ({ onLogged }: Props) => {
  const { t } = useTranslation('account');
  const { showLoginWithEmailPopup } = useLoginWithEmailPopup(onLogged);
  const [submittedEmail, setSubmittedEmail] = useState<string | null>(null);
  const { values, handleChange, submitForm, errors, isValid, isSubmitting, dirty } = useForgotPasswordForm(
    (email: string) => setSubmittedEmail(email)
  );

  if (submittedEmail) {
    return (
      <Container data-testid={'check-email-popup'}>
        <Header data-testid={'header'}>{t('check-email.header')}</Header>
        <Description data-testid={'description'}>{t('check-email.description')}</Description>
        <EmailText data-testid={'email'}>{submittedEmail}</EmailText>
        <SubmitButton data-testid={'ok-button'} onClick={showLoginWithEmailPopup}>
          {t('check-email.ok-button')}
        </SubmitButton>
      </Container>
    );
  }

  return (
    <Container data-testid={'forgot-password-popup'}>
      <PopupBackButton onClick={showLoginWithEmailPopup} label={t('forgot-password.back-button')} />
      <Header data-testid={'header'}>{t('forgot-password.header')}</Header>
      <Description data-testid={'description'}>{t('forgot-password.description')}</Description>
      <EmailInput
        testId={'email-field'}
        value={values.email}
        placeholder={t('forgot-password.email-placeholder')}
        inputType={'email'}
        onChange={handleChange('email')}
        errorMessage={errors.email}
      />
      <SubmitButton
        data-testid={'submit-button'}
        onClick={submitForm}
        disabled={!dirty || !isValid || isSubmitting}
        type={'submit'}>
        {t('forgot-password.submit-button')}
      </SubmitButton>
    </Container>
  );
};

export default ForgotPasswordPopup;
